import { useCallback, useState } from 'react';
import { FileRejection, useDropzone } from 'react-dropzone';
import req from 'utils/req';
import { z } from 'zod';

type PhotoUploadType = 'profile' | 'license' | 'host' | 'experience';

type UploadStatus = {
  isLoading: boolean;
  error?: string;
};

interface PhotoUploaderProps {
  onPhotoUpload: (photo: { name: string; value: string }) => void;
  photoType?: PhotoUploadType;
  experienceId?: string;
}

const cloudinarySignResponseSchema = z
  .object({
    upload_url: z.string(),
    public_id: z.string(),
    timestamp: z.union([z.number(), z.string()]),
    signature: z.string(),
    api_key: z.string(),
    tags: z.array(z.string()).optional(),
  })
  .passthrough();

type CloudinarySignResponse = z.infer<typeof cloudinarySignResponseSchema>;

const getSigningUrl = (
  fileName: string,
  photoType: PhotoUploadType,
  experienceId?: string,
): string => {
  const encodedFileName = encodeURIComponent(fileName);

  switch (photoType) {
    case 'profile':
      return `${URL_API_HOST}/api/v1/photo/signforcloudinary/profile?filename=${encodedFileName}`;
    case 'license':
      return `${URL_API_HOST}/api/v1/photo/signforcloudinary/license?filename=${encodedFileName}`;
    case 'host':
      return `${URL_API_HOST}/api/v1/photo/signforcloudinary/host?filename=${encodedFileName}`;
    default:
      throw new Error('Invalid photo upload type.');
  }
};

const uploadFile = async (
  file: File,
  options: { photoType: PhotoUploadType; experienceId?: string },
): Promise<string> => {
  const { photoType, experienceId } = options;
  const signingUrl = getSigningUrl(file.name, photoType, experienceId);
  const signingData = await req(signingUrl).get();
  const signingResponse: CloudinarySignResponse =
    cloudinarySignResponseSchema.parse(signingData);

  const { upload_url: uploadUrl, tags = [], ...formFields } = signingResponse;

  if (!uploadUrl) {
    throw new Error('Photo upload failed: missing upload URL.');
  }

  const formData = new FormData();
  formData.append('file', file);

  Object.entries(formFields).forEach(([key, value]) => {
    if (value === undefined || value === null) {
      return;
    }

    if (Array.isArray(value)) {
      formData.append(key, value.join(','));
    } else {
      formData.append(key, String(value));
    }
  });

  if (tags.length) {
    formData.append('tags', tags.join(','));
  }

  const uploadResponse = await fetch(uploadUrl, {
    method: 'POST',
    body: formData,
  });

  const responseText = await uploadResponse.text();

  let parsedResponse: Record<string, any> = {};
  try {
    parsedResponse = responseText ? JSON.parse(responseText) : {};
  } catch (error) {
    parsedResponse = { raw: responseText };
  }

  if (!uploadResponse.ok) {
    const message =
      parsedResponse?.error?.message ||
      parsedResponse?.error ||
      'Photo upload failed. Please try again.';
    throw new Error(message);
  }

  const photoUrl = parsedResponse.secure_url || parsedResponse.url;

  if (!photoUrl) {
    throw new Error('Photo upload did not return a file URL.');
  }

  return photoUrl;
};

function usePhotoUploader({
  onPhotoUpload,
  photoType = 'host',
  experienceId,
}: PhotoUploaderProps) {
  const [status, setStatus] = useState<UploadStatus>({
    isLoading: false,
    error: undefined,
  });

  const onDropAccepted = useCallback(
    async (acceptedFiles: File[]) => {
      setStatus({ isLoading: true, error: undefined });

      try {
        const uploadedPhotos = await Promise.all(
          acceptedFiles.map((file) =>
            uploadFile(file, { photoType, experienceId }),
          ),
        );

        uploadedPhotos.forEach((url) => {
          onPhotoUpload({ name: 'photo', value: url });
        });

        setStatus({ isLoading: false, error: undefined });
      } catch (error) {
        const message =
          error instanceof Error
            ? error.message
            : 'Photo upload failed. Please try again.';
        setStatus({ isLoading: false, error: message });
      }
    },
    [experienceId, onPhotoUpload, photoType],
  );

  const onDropRejected = useCallback(
    (_fileRejections: FileRejection[]) => {
      setStatus({
        isLoading: false,
        error:
          'Oops! Unsupported image type. Please upload a JPG or PNG file. Consider trying a different image or converting it to a supported format.',
      });
    },
    [setStatus],
  );

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDropAccepted,
    onDropRejected,
    multiple: false,
    accept: {
      'image/jpeg': [],
      'image/png': [],
    },
  });

  return {
    getRootProps,
    getInputProps,
    isDragActive,
    uploadStatus: status,
  };
}

export default usePhotoUploader;
